// src/components/CompostManagementForm.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from "@mui/material";
import apiClient from "../utils/apiClient";
import "../style/Crops.css";
import MonthBoxGrid from "./MonthBoxGrid";

const monthNames = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];

const getMonthIndex = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getMonth();
};

const inRange = (start, end, month) => {
  if (start === null || end === null || month === null) return false;
  if (start <= end) return month >= start && month <= end;
  return month >= start || month <= end;
};

const CompostManagementForm = ({ open, onClose, plots, onSave, garden_uuid, planted, year }) => {
  const [formState, setFormState] = useState({
    plot_uuid: "",
    crop_uuid: "",
    date_planted: "",
    rows: "",
    method: "",
    orientation: "",
    placement_mode: "",
  });

  const [crops, setCrops] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [occupancy, setOccupancy] = useState([]);
  const [plotHasCrops, setPlotHasCrops] = useState(false);

  // Filter plots to only show compost bin plots
  const compostBinPlots = plots.filter(plot => 
    plot.plot_type?.name?.toLowerCase() === 'compost bin'
  );

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const response = await apiClient.get("/crops");
        // Filter crops to only show compost types
        const compostCrops = response.data.filter(crop => 
          crop.crop_type?.name?.toLowerCase() === 'compost'
        );
        setCrops(compostCrops);
      } catch (err) {
        console.error("Failed to fetch crops:", err);
      }
    };
    fetchCrops();
  }, []);

  useEffect(() => {
    if (planted) {
      setFormState({
        plot_uuid: planted.plot_uuid,
        crop_uuid: planted.crop_uuid,
        date_planted: planted.date_planted,
        rows: planted.rows || "",
        method: planted.method || "",
        orientation: planted.orientation || "",
        placement_mode: planted.placement_mode || "",
      });
    } else {
      setFormState({
        plot_uuid: "",
        crop_uuid: "",
        date_planted: "",
        rows: "",
        method: "",
        orientation: "",
        placement_mode: "",
      });
    }
  }, [planted]);

  useEffect(() => {
    const fetchOccupancy = async () => {
      if (formState.plot_uuid && formState.date_planted) {
        try {
          const res = await apiClient.get(
            `/plots/${formState.plot_uuid}/occupancy`,
            { params: { date: formState.date_planted } }
          );
          const occ = res.data.occupied || [];
          setOccupancy(occ);
          setPlotHasCrops(occ.length > 0);

          if (occ.length > 0 && occ[0].planted.orientation) {
            setFormState((prev) => ({
              ...prev,
              orientation: occ[0].planted.orientation,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch occupancy:", err);
          setOccupancy([]);
          setPlotHasCrops(false);
        }
      } else {
        setOccupancy([]);
        setPlotHasCrops(false);
      }
    };
    fetchOccupancy();
  }, [formState.plot_uuid, formState.date_planted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const getWindowDates = (crop, periodType) => {
    const windows = (crop.crop_windows || []).filter(w => w.period_type === periodType);
    if (windows.length === 0) return { start: null, end: null };
    return {
      start: getMonthIndex(windows[0].window_start),
      end: getMonthIndex(windows[0].window_end)
    };
  };

  const handleCropSelect = (crop) => {
    const selectedMonth = formState.date_planted
      ? new Date(formState.date_planted).getMonth()
      : null;

    const planting = getWindowDates(crop, 'planting');
    const sowing = getWindowDates(crop, 'direct_sowing');

    const canPlant = inRange(planting.start, planting.end, selectedMonth);
    const canSow = inRange(sowing.start, sowing.end, selectedMonth);

    let newMethod = formState.method;
    if (selectedMonth !== null) {
      if (canPlant && !canSow) newMethod = "planting";
      else if (!canPlant && canSow) newMethod = "direct_sowing";
    }

    setFormState((prev) => ({
      ...prev,
      crop_uuid: crop.crop_uuid,
      method: newMethod,
    }));
  };

  const getMaxRows = () => {
    const plot = compostBinPlots.find((p) => p.plot_uuid === formState.plot_uuid);
    const crop = crops.find((c) => c.crop_uuid === formState.crop_uuid);

    if (!plot || !crop || !formState.orientation) return null;

    const plotWidth = plot.unit === "meters" ? plot.width : plot.width * 0.3048;
    const plotDepth = plot.unit === "meters" ? plot.depth : plot.depth * 0.3048;

    const longSide = Math.max(plotWidth, plotDepth);
    const shortSide = Math.min(plotWidth, plotDepth);

    const rowSpacing = crop.row_spacing_cm
      ? crop.row_spacing_cm / 100
      : 0.25;

    const limitingDimension =
      formState.orientation === "lengthwise" ? shortSide : longSide;

    const maxRowsFull = Math.max(1, Math.floor(limitingDimension / rowSpacing));

    if (formState.placement_mode === "next_to" && occupancy.length > 0) {
      const usedRows = occupancy.reduce(
        (sum, occ) => sum + (occ.planted.rows || 0),
        0
      );
      return Math.max(0, maxRowsFull - usedRows);
    }

    return maxRowsFull;
  };

  const normalizePayload = (data) => {
    const normalized = { ...data };
    Object.keys(normalized).forEach((key) => {
      if (normalized[key] === "" || normalized[key] === null) {
        delete normalized[key];
      }
    });
    return normalized;
  };

  const isFormValid = () => {
    if (!formState.plot_uuid || !formState.crop_uuid || !formState.date_planted) {
      return false;
    }
    if (!formState.rows && !formState.amount) {
      return false;
    }
    if (formState.rows) {
      const rowsInt = parseInt(formState.rows, 10);
      const maxRows = getMaxRows();
      if (isNaN(rowsInt) || rowsInt < 1 || (maxRows !== null && rowsInt > maxRows)) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      const payload = normalizePayload(formState);
      if (!plotHasCrops) {
        delete payload.placement_mode;
      }
      if (!payload.method) delete payload.method;

      if (planted) {
        await apiClient.put(
          `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`,
          payload
        );
      } else {
        await apiClient.post(`/gardens/${garden_uuid}/crops/`, payload);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to save compost entry:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(
        `/gardens/${garden_uuid}/crops/${planted.planted_uuid}`
      );
      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to delete compost entry:", err);
    }
  };

  const selectedMonth = formState.date_planted
    ? new Date(formState.date_planted).getMonth()
    : null;

  const filteredCrops = crops.filter((crop) => {
    const planting = getWindowDates(crop, 'planting');
    const sowing = getWindowDates(crop, 'direct_sowing');
    const setting = getWindowDates(crop, 'setting');

    let valid = true;
    if (selectedMonth !== null) {
      if (formState.method === "planting") {
        valid = inRange(planting.start, planting.end, selectedMonth);
      } else if (formState.method === "direct_sowing") {
        valid = inRange(sowing.start, sowing.end, selectedMonth);
      } else if (formState.method === "setting") {
        valid = inRange(setting.start, setting.end, selectedMonth);
      } else {
        valid =
          inRange(planting.start, planting.end, selectedMonth) ||
          inRange(sowing.start, sowing.end, selectedMonth) ||
          inRange(setting.start, setting.end, selectedMonth);
      }
    }

    const matchesSearch = crop.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch && valid;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="CompostManagementForm">
      <DialogTitle>{planted ? "Edit Compost Entry" : "Manage Compost"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth select
          label="Select Compost Bin"
          name="plot_uuid"
          value={formState.plot_uuid}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="">Select a compost bin</MenuItem>
          {compostBinPlots.map((plot) => (
            <MenuItem key={plot.plot_uuid} value={plot.plot_uuid}>
              {plot.name} ({(plot.width * plot.depth).toFixed(2)} m²)
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Date"
          name="date_planted"
          type="date"
          value={formState.date_planted}
          onChange={handleChange}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth select
          label="Method"
          name="method"
          value={formState.method}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="">Not set</MenuItem>
          <MenuItem value="direct_sowing">Direct Sowing</MenuItem>
          <MenuItem value="planting">Planting</MenuItem>
          <MenuItem value="setting">Setting</MenuItem>
        </TextField>

        {!plotHasCrops ? (
          <TextField
            fullWidth select
            label="Orientation"
            name="orientation"
            value={formState.orientation}
            onChange={handleChange}
            margin="normal"
          >
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="lengthwise">Lengthwise</MenuItem>
            <MenuItem value="widthwise">Widthwise</MenuItem>
          </TextField>
        ) : (
          <>
            <TextField
              fullWidth
              label="Orientation"
              name="orientation"
              value={formState.orientation}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth select
              label="Placement Mode"
              name="placement_mode"
              value={formState.placement_mode}
              onChange={handleChange}
              margin="normal"
            >
              <MenuItem value="next_to">Next To</MenuItem>
              <MenuItem value="between">Between</MenuItem>
            </TextField>
          </>
        )}

        <TextField
          fullWidth
          label="Rows"
          name="rows"
          type="number"
          value={formState.rows}
          onChange={handleChange}
          margin="normal"
          InputProps={{
            inputProps: { min: 1, max: getMaxRows() || undefined }
          }}
          helperText={
            getMaxRows() !== null
              ? `Max rows for this crop/plot: ${getMaxRows()}${
                  formState.placement_mode === "next_to" && occupancy.length > 0
                    ? ` (already used: ${occupancy.reduce(
                        (sum, occ) => sum + (occ.planted.rows || 0),
                        0
                      )})`
                    : ""
                }`
              : "Select plot, crop, and orientation first"
          }
        />

        <Typography variant="h6" gutterBottom>
          Select Compost Activity ({filteredCrops.length} available)
        </Typography>
        <TextField
          fullWidth
          label="Search Compost Activities"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, type, or Latin name"
          variant="outlined"
          margin="normal"
        />

        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'block',
            marginBottom: 2
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Pre-germination</TableCell>
                <TableCell>Direct Sowing</TableCell>
                <TableCell>Planting</TableCell>
                <TableCell>Growth Time</TableCell>
                <TableCell>Spacing</TableCell>
                <TableCell>12-Month Grid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCrops.map((crop) => {
                const preGerm = getWindowDates(crop, 'pre_germination');
                const sowing = getWindowDates(crop, 'direct_sowing');
                const planting = getWindowDates(crop, 'planting');
                const setting = getWindowDates(crop, 'setting');
                
                const ranges = {
                  preGerminationStart: preGerm.start,
                  preGerminationEnd: preGerm.end,
                  directSowingStart: sowing.start,
                  directSowingEnd: sowing.end,
                  plantingStart: planting.start,
                  plantingEnd: planting.end,
                  settingStart: setting.start,
                  settingEnd: setting.end,
                  harvestStart: null,
                  harvestEnd: null,
                };

                const growthTime =
                  crop.harvest_min_days && crop.harvest_max_days
                    ? crop.harvest_min_days === crop.harvest_max_days
                      ? `${crop.harvest_min_days} days`
                      : `${crop.harvest_min_days}-${crop.harvest_max_days} days`
                    : "N/A";

                const isSelected = crop.crop_uuid === formState.crop_uuid;
                
                return (
                  <TableRow
                    key={crop.crop_uuid}
                    hover
                    onClick={() => handleCropSelect(crop)}
                    className={`crop-row ${isSelected ? 'crop-row-selected' : ''}`}
                  >
                    <TableCell>{crop.name}</TableCell>
                    <TableCell>
                      {ranges.preGerminationStart !== null &&
                      ranges.preGerminationEnd !== null
                        ? `${monthNames[ranges.preGerminationStart]} to ${monthNames[ranges.preGerminationEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {ranges.directSowingStart !== null &&
                      ranges.directSowingEnd !== null
                        ? `${monthNames[ranges.directSowingStart]} to ${monthNames[ranges.directSowingEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {ranges.plantingStart !== null &&
                      ranges.plantingEnd !== null
                        ? `${monthNames[ranges.plantingStart]} to ${monthNames[ranges.plantingEnd]}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>{growthTime}</TableCell>
                    <TableCell>
                      {crop.row_spacing_cm
                        ? `${crop.row_spacing_cm} cm between rows`
                        : "N/A"},{" "}
                      {crop.plant_spacing_cm
                        ? `${crop.plant_spacing_cm} cm in row`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <MonthBoxGrid ranges={ranges} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {planted && <Button onClick={handleDelete} color="secondary">Delete</Button>}
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isFormValid()}
        >
          {planted ? "Update Compost Entry" : "Add Compost Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompostManagementForm;
